// src/pages/patient/components/ReportViewer.tsx
import React, { useState } from "react";
import { Modal, Progress, Space } from "antd";
import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { ReportFile } from "../../../types/file/FileTypes";
import { downloadReport } from "../../../api/file/fileServiceApi";
import { useNotifications } from "../../../context/NotificationContext";
import AudioPlayer from "../../../components/AudioPlayer";
import PdfViewer from "../../../components/PdfViewer";
import { AudioCacheManager } from "../../../services/AudioCacheManager";
import type { UserUuid } from "../../../types/identity";

interface ReportViewerProps {
	visible: boolean;
	onClose: () => void;
	report: ReportFile;
	patientId: UserUuid;
}

const ReportViewer: React.FC<ReportViewerProps> = ({ visible, onClose, report, patientId }) => {
	const { showError, showSuccess, showInfo } = useNotifications();
	const [downloading, setDownloading] = useState(false);
	const [downloadProgress, setDownloadProgress] = useState(0);
	const [isMobile, setIsMobile] = useState(() => {
		const userAgent = navigator.userAgent.toLowerCase();
		const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
		const isSmallScreen = window.innerWidth <= 768;
		return isMobileDevice || isSmallScreen;
	});

	const isAudioReport = report.file_name.toLowerCase().includes("audio");
	const isPdfReport = report.file_type === "application/pdf";

	const handleDownload = async () => {
		if (isAudioReport) {
			await handleAudioDownload();
		} else {
			await handleRegularDownload();
		}
	};

	const handleAudioDownload = async () => {
		setDownloading(true);
		setDownloadProgress(0);

		try {
			const cacheManager = AudioCacheManager.getInstance();
			await cacheManager.initialize();

			const cachedEntry = await cacheManager.get(report.id);

			if (cachedEntry) {
				showSuccess("Downloading from cache...");
				const url = URL.createObjectURL(cachedEntry.blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = report.file_name.replace(/\.[^/.]+$/, ".mp3");
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
				showSuccess("Audio file downloaded successfully");
			} else {
				showInfo("Processing audio file... This may take up to 30 seconds.");

				const onProgress = (progressEvent: any) => {
					if (progressEvent.lengthComputable) {
						const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
						setDownloadProgress(percent);
					}
				};

				const result = await downloadReport(patientId, report.id, onProgress);
				showSuccess(`${result.fileName} downloaded successfully`);
			}
		} catch (error) {
			console.error("Download failed:", error);
			showError("Failed to download audio file");
		} finally {
			setDownloading(false);
			setDownloadProgress(0);
		}
	};

	const handleRegularDownload = async () => {
		setDownloading(true);
		setDownloadProgress(0);

		try {
			const onProgress = (progressEvent: any) => {
				if (progressEvent.lengthComputable) {
					const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					setDownloadProgress(percent);
				}
			};

			const result = await downloadReport(patientId, report.id, onProgress);
			showSuccess(`${result.fileName} downloaded successfully`);
		} catch (error) {
			console.error("Download failed:", error);
			showError("Failed to download report");
		} finally {
			setDownloading(false);
			setDownloadProgress(0);
		}
	};

	const renderContent = () => {
		if (isAudioReport) {
			return <AudioPlayer report={report} patientId={patientId} onDownload={handleDownload} downloading={downloading} onError={showError} onSuccess={showSuccess} />;
		}

		if (isPdfReport) {
			return <PdfViewer report={report} patientId={patientId} onDownload={handleDownload} downloading={downloading} downloadProgress={downloadProgress} onError={showError} />;
		}

		return (
			<div className="flex items-center justify-center h-full" style={{ minHeight: 400 }}>
				<span className="text-sm">Unable to display this report type</span>
			</div>
		);
	};

	return (
		<Modal
			data-testid="files-report-viewer"
			open={visible}
			onCancel={onClose}
			width={isAudioReport ? 800 : isMobile ? "95%" : "90%"}
			style={{ top: isMobile ? 0 : 20 }}
			styles={{
				body: {
					padding: isMobile ? "12px" : "24px",
					maxHeight: isMobile ? "calc(100vh - 120px)" : "none",
					overflow: "auto",
				},
			}}
			footer={null}
			title={
				<div className="flex items-center justify-between">
					<span className="truncate" style={{ maxWidth: isMobile ? "200px" : "none" }}>
						{report.file_name}
					</span>
					{!isMobile && !isAudioReport && (
						<Space>
							{downloading ? (
								<div style={{ width: 200 }}>
									<Progress percent={downloadProgress} size="small" />
								</div>
							) : (
								<span style={{ cursor: "pointer" }} onClick={handleDownload} title="Download this file">
									<DownloadOutlined /> Download
								</span>
							)}
						</Space>
					)}
				</div>
			}
			closeIcon={<CloseOutlined />}
			destroyOnClose
		>
			{renderContent()}
		</Modal>
	);
};

export default ReportViewer;
